/**
 * ActivityProgressWizard — modal shell for logging progress against care plan activities.
 * Renders task-type-specific progress content from a registry and falls back to
 * DefaultProgressContent for unregistered activity/task codes. Handles snooze flow
 * after progress is logged.
 * @agentic connections
 */

import { Button, Group, Stack, Text } from "@mantine/core";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import type {
  Task,
  Patient,
  Practitioner,
  PractitionerRole,
} from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import { useCallback, useEffect, useState } from "react";
import { useConfirmation } from "@benchmark/ui/hooks";
import { useBeforeUnload } from "@benchmark/ui/hooks";
import { ConfirmationPresets } from "@benchmark/ui/utils";
import type { ComponentType } from "react";
import { Modal } from "@benchmark/ui/components/atomic";
import { logActivityProgress } from "@benchmark/core/actions";
import { getResourceIdFromReference } from "@benchmark/core/utils";
import {
  showSuccessNotification,
  showErrorNotification,
} from "@benchmark/ui/utils";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { usePatientCoverage } from "@/hooks/domain/usePatientCoverage";
import { findBenchmarkPractitionerRole } from "@benchmark/core/services";
import { getPatientDisplayName } from "@benchmark/core/resolvers";
import {
  parseFhirInstantForDisplay,
  toDateInputValue,
} from "@benchmark/ui/utils";
import type { ProgressContentProps } from "@/types/domain/progress-log";
import type { ProgressLogData } from "@benchmark/core/types";
import { useActivityProgressLogs } from "@/hooks/domain/useActivityProgressLogs";
import { useTaskCareTimeLogs } from "@/hooks/domain/useTaskCareTimeLogs";
import { DefaultProgressContent } from "./progress-content/DefaultProgressContent";
import { OutreachAttemptProgressContent } from "./progress-content/OutreachAttemptProgressContent";
import { ProviderOutreachProgressContent } from "./progress-content/ProviderOutreachProgressContent";
import { AdministrativeQuickLogProgressContent } from "./progress-content/AdministrativeQuickLogProgressContent";
import { ProgressLogHistory } from "./progress-content/ProgressLogHistory";
import { TaskModalHeader } from "./TaskModalHeader";
import styles from "./ActivityProgressWizard.module.css";
import { CONFIG } from "@/app/config";

/**
 * Registry entry for a progress content component.
 * @agentic connections
 */
type ProgressContentRegistryEntry = {
  component: ComponentType<ProgressContentProps>;
};

/**
 * Registry mapping task type codes to progress content configuration.
 * @agentic connections
 */
const PROGRESS_CONTENT_REGISTRY: Record<string, ProgressContentRegistryEntry> =
  {
    "schedule-intake": { component: OutreachAttemptProgressContent },
    "complete-intake": { component: OutreachAttemptProgressContent },
    "check-in-on-member": { component: OutreachAttemptProgressContent },
    "offer-services": { component: OutreachAttemptProgressContent },
    "confirm-services": { component: OutreachAttemptProgressContent },
    "follow-up-on-services": { component: OutreachAttemptProgressContent },
    "complete-cadenced-followup": { component: OutreachAttemptProgressContent },
    "follow-up-on-cocm": { component: OutreachAttemptProgressContent },
    "discharge-member": { component: OutreachAttemptProgressContent },
    "schedule-services": { component: ProviderOutreachProgressContent },
    "consult-psych": { component: ProviderOutreachProgressContent },
    "consult-pcp": { component: ProviderOutreachProgressContent },
    "finalize-care-plan-enrollment": {
      component: AdministrativeQuickLogProgressContent,
    },
    "select-services": {
      component: AdministrativeQuickLogProgressContent,
    },
    "approve-selected-services": {
      component: AdministrativeQuickLogProgressContent,
    },
    // Note: `enroll-in-mirah` was retired with the Mirah partnership
    // wind-down. Straggler tasks fall through to `DefaultProgressContent`.
    "build-cocm-care-plan": {
      component: AdministrativeQuickLogProgressContent,
    },
  };

/**
 * Props for ActivityProgressWizard.
 * @agentic connections
 */
export type ActivityProgressWizardProps = {
  opened: boolean;
  onClose: () => void;
  task: Task | null;
  onComplete: () => void;
};

/**
 * Snooze prompt shown after a progress log with "prompt" snooze behavior.
 * @agentic connections
 */
const SnoozePrompt = ({
  onConfirm,
  onSkip,
}: {
  onConfirm: (date: string) => void;
  onSkip: () => void;
}) => {
  const [snoozeDate, setSnoozeDate] = useState<string>("");
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);

  const applyPreset = (days: number, label: string) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    setSnoozeDate(toDateInputValue(date));
    setSelectedPreset(label);
  };

  return (
    <Stack gap="md" className={styles.snoozeSection}>
      <Text size="sm" fw={500}>
        When should this task resurface?
      </Text>
      <Group gap="sm">
        <Button
          variant={selectedPreset === "1d" ? "filled" : "light"}
          color={selectedPreset === "1d" ? "purple" : undefined}
          size="xs"
          onClick={() => applyPreset(1, "1d")}
        >
          1 day
        </Button>
        <Button
          variant={selectedPreset === "3d" ? "filled" : "light"}
          color={selectedPreset === "3d" ? "purple" : undefined}
          size="xs"
          onClick={() => applyPreset(3, "3d")}
        >
          3 days
        </Button>
        <Button
          variant={selectedPreset === "1w" ? "filled" : "light"}
          color={selectedPreset === "1w" ? "purple" : undefined}
          size="xs"
          onClick={() => applyPreset(7, "1w")}
        >
          Next week
        </Button>
      </Group>
      <TextInput
        type="date"
        label="Or pick a date"
        value={snoozeDate}
        onChange={(e) => {
          setSnoozeDate(e.currentTarget.value);
          setSelectedPreset(null);
        }}
        min={toDateInputValue(new Date())}
      />
      <Group justify="flex-end" gap="sm">
        <Button variant="subtle" size="sm" onClick={onSkip}>
          Skip
        </Button>
        <Button
          color="purple"
          size="sm"
          disabled={!snoozeDate}
          onClick={() => {
            onConfirm(snoozeDate);
          }}
        >
          Set Follow-Up Date
        </Button>
      </Group>
    </Stack>
  );
};

/**
 * Unified activity progress wizard component.
 * @agentic connections
 */
export const ActivityProgressWizard = ({
  opened,
  onClose,
  task,
  onComplete,
}: ActivityProgressWizardProps) => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const { confirm, ConfirmationModal } = useConfirmation();
  const [pendingData, setPendingData] = useState<ProgressLogData | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  useBeforeUnload(opened && (isDirty || !!pendingData));
  const [practitionerRole, setPractitionerRole] = useState<
    PractitionerRole | undefined
  >(undefined);

  const patientId = task?.for?.reference
    ? getResourceIdFromReference(task.for.reference, "Patient")
    : undefined;
  const { data: patientResourceRaw } = usePatientResource(patientId);
  const patientResource = patientResourceRaw as Patient | undefined;
  const { primary: primaryCoverage } = usePatientCoverage(
    patientResource ?? ({ resourceType: "Patient" } as Patient),
  );
  const { logs: progressLogs, count: logCount } = useActivityProgressLogs(
    opened ? task?.id : undefined,
  );
  const { logs: careTimeLogs, count: careTimeCount } = useTaskCareTimeLogs(
    opened ? task?.id : undefined,
  );

  useEffect(() => {
    if (!profile?.id || !opened) return;
    findBenchmarkPractitionerRole(medplum, {
      reference: `Practitioner/${profile.id}`,
      benchmarkOrganizationId: CONFIG.VITE_BENCHMARK_ORGANIZATION_ID,
    }).then(setPractitionerRole);
  }, [medplum, profile?.id, opened]);

  const submitProgress = useCallback(
    async (data: ProgressLogData) => {
      if (!task || !patientResource || !profile || !practitionerRole) return;

      setSubmitting(true);
      try {
        const result = await logActivityProgress(medplum, {
          task,
          progressData: data,
          patient: patientResource,
          practitioner: profile,
          practitionerRole,
        });

        const snoozedMsg = result.taskSnoozed
          ? ` Task snoozed to ${parseFhirInstantForDisplay(result.newDueDate!).toLocaleDateString()}.`
          : "";
        showSuccessNotification(`Progress logged.${snoozedMsg}`);
        setIsDirty(false);
        onComplete();
        onClose();
      } catch (err) {
        showErrorNotification(
          err instanceof Error ? err.message : "Failed to log progress",
        );
      } finally {
        setSubmitting(false);
        setPendingData(null);
      }
    },
    [
      medplum,
      task,
      patientResource,
      profile,
      practitionerRole,
      onComplete,
      onClose,
    ],
  );

  const handleContentSubmit = useCallback(
    async (data: ProgressLogData) => {
      if (data.snoozeBehavior.type === "prompt" && !data.followUpDate) {
        setIsDirty(false);
        setPendingData(data);
        return;
      }
      await submitProgress(data);
    },
    [submitProgress],
  );

  const handleSnoozeConfirm = useCallback(
    (dateIso: string) => {
      if (!pendingData) return;
      submitProgress({ ...pendingData, followUpDate: dateIso });
    },
    [pendingData, submitProgress],
  );

  const handleSnoozeSkip = useCallback(() => {
    if (!pendingData) return;
    submitProgress({
      ...pendingData,
      snoozeBehavior: { type: "none" },
    });
  }, [pendingData, submitProgress]);

  const handleClose = useCallback(async () => {
    if (isDirty || pendingData) {
      const confirmed = await confirm({
        ...ConfirmationPresets.navigate,
        message: "You have unsaved progress. Are you sure you want to close?",
      });
      if (!confirmed) return;
    }
    setPendingData(null);
    setIsDirty(false);
    onClose();
  }, [isDirty, pendingData, confirm, onClose]);

  const handleDirtyChange = useCallback((dirty: boolean) => {
    setIsDirty(dirty);
  }, []);

  if (!task) return null;

  const taskTypeCode = task.code?.coding?.[0]?.code;

  const registryEntry = taskTypeCode
    ? PROGRESS_CONTENT_REGISTRY[taskTypeCode]
    : undefined;
  const ContentComponent = registryEntry?.component ?? DefaultProgressContent;
  const patientName = patientResource
    ? getPatientDisplayName(patientResource)
    : undefined;
  const referringPracticeLabel = patientResource?.managingOrganization?.display;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <TaskModalHeader
          actionLabel="Log Progress"
          task={task}
          patientName={patientName}
          coverageLabel={primaryCoverage.insuranceInfo}
          insuranceMemberId={primaryCoverage.memberNumber}
          referringPracticeLabel={referringPracticeLabel}
        />
      }
      size="xl"
    >
      <Stack gap="md" className={styles.wizardBody}>
        {(logCount > 0 || careTimeCount > 0) && !pendingData && (
          <ProgressLogHistory logs={progressLogs} careTimeLogs={careTimeLogs} />
        )}

        {pendingData ? (
          <SnoozePrompt
            onConfirm={handleSnoozeConfirm}
            onSkip={handleSnoozeSkip}
          />
        ) : (
          <ContentComponent
            task={task}
            onSubmit={handleContentSubmit}
            onCancel={handleClose}
            priorLogCount={logCount}
            onDirtyChange={handleDirtyChange}
          />
        )}

        {submitting && (
          <Text size="xs" c="dimmed" ta="center">
            Saving...
          </Text>
        )}
      </Stack>
      <ConfirmationModal />
    </Modal>
  );
};
